import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Users } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  PreviewCell,
  ROW_LINK_CLASS,
  TitleSlugFields,
  useAdminListCreate,
} from "~/components/cih/admin-list-page";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import {
  playbooksQueryOptions,
  type PlaybookListRow,
} from "~/lib/functions/cih/playbooks";
import {
  createThoughtLeaderFn,
  thoughtLeaderQueryKeys,
  thoughtLeadersQueryOptions,
  type ThoughtLeaderRow,
} from "~/lib/functions/cih/thought-leaders";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import {
  IP_SENSITIVITY_OPTIONS,
  PARTNER_TIER_OPTIONS,
  type IpSensitivity,
  type PartnerTier,
} from "~/lib/shared/content-status";
import { nextSortSearch } from "~/lib/shared/sort";

const THOUGHT_LEADER_SORTS = ["name"] as const;
type ThoughtLeaderSort = (typeof THOUGHT_LEADER_SORTS)[number];

const THOUGHT_LEADER_SORT_DEFAULTS =
  listSortDefaults<ThoughtLeaderSort>("name");

const THOUGHT_LEADER_SORT_COLUMNS: Record<
  ThoughtLeaderSort,
  SortColumn<ThoughtLeaderRow>
> = {
  name: { kind: "text", value: (row) => row.name },
};

export const Route = createFileRoute("/admin/cih/thought-leaders/")({
  staticData: { title: "Thought Leaders" },
  validateSearch: listSearchSchema(THOUGHT_LEADER_SORTS),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(thoughtLeadersQueryOptions()),
      context.queryClient.ensureQueryData(playbooksQueryOptions()),
    ]);
    return {};
  },
  component: ThoughtLeadersPage,
});

function thoughtLeaderColumns(
  playbooksByThoughtLeader: ReadonlyMap<string, PlaybookListRow[]>,
): ColumnDef<ThoughtLeaderRow>[] {
  return [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <Link
          to="/admin/cih/thought-leaders/$thoughtLeaderId"
          params={{ thoughtLeaderId: row.original.id }}
          className={ROW_LINK_CLASS}
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: "organization",
      header: "Organization",
      cell: ({ row }) => (
        <span className="text-gray-500">
          {row.original.organization ?? "—"}
        </span>
      ),
    },
    {
      id: "playbooks",
      header: "Playbooks",
      cell: ({ row }) => (
        <ThoughtLeaderPlaybooks
          playbooks={playbooksByThoughtLeader.get(row.original.id) ?? []}
        />
      ),
    },
    {
      id: "ipSensitivity",
      header: "IP sensitivity",
      cell: ({ row }) => (
        <span className="text-gray-500 capitalize">
          {row.original.ipSensitivity}
        </span>
      ),
    },
    {
      id: "partnerTier",
      header: "Partner tier",
      cell: ({ row }) => (
        <span className="text-gray-500">
          {row.original.partnerTier
            ? row.original.partnerTier.replace(/tier/, "Tier ")
            : "—"}
        </span>
      ),
    },
    {
      id: "website",
      header: "Website",
      cell: ({ row }) => (
        <PreviewCell href={row.original.website} label="Visit" />
      ),
    },
  ];
}

function ThoughtLeadersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const urlSearch = Route.useSearch();
  const sort = urlSearch.sort ?? THOUGHT_LEADER_SORT_DEFAULTS.sort;
  const direction =
    urlSearch.dir ?? THOUGHT_LEADER_SORT_DEFAULTS.direction(sort);
  const [search, setSearch] = useState("");
  const create = useAdminListCreate(() => form.reset());

  const { data: thoughtLeaders, isLoading } = useQuery(
    thoughtLeadersQueryOptions(),
  );
  const { data: playbooks } = useQuery(playbooksQueryOptions());

  const playbooksByThoughtLeader = useMemo(() => {
    const byLeader = new Map<string, PlaybookListRow[]>();
    for (const playbook of playbooks ?? []) {
      const list = byLeader.get(playbook.thoughtLeaderId) ?? [];
      list.push(playbook);
      byLeader.set(playbook.thoughtLeaderId, list);
    }
    return byLeader;
  }, [playbooks]);

  const createMutation = useMutation({
    mutationFn: createThoughtLeaderFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: thoughtLeaderQueryKeys.all });
      create.finish("Thought leader created.");
      navigate({
        to: "/admin/cih/thought-leaders/$thoughtLeaderId",
        params: { thoughtLeaderId: created.id },
      });
    },
  });

  const form = useForm({
    defaultValues: {
      name: "",
      slug: "",
      organization: "",
      website: "",
      ipSensitivity: "medium" as IpSensitivity,
      partnerTier: "" as PartnerTier | "",
      cautiousFlag: false,
    },
    onSubmit: ({ value }) => {
      createMutation.mutate({
        data: {
          name: value.name.trim(),
          slug: value.slug.trim() || undefined,
          organization: value.organization.trim() || undefined,
          website: value.website.trim() || undefined,
          ipSensitivity: value.ipSensitivity,
          partnerTier: value.partnerTier || undefined,
          cautiousFlag: value.cautiousFlag,
        },
      });
    },
  });

  const canCreate = useStore(form.store, ({ values }) =>
    Boolean(values.name.trim()),
  );

  const columns = useMemo(
    () => thoughtLeaderColumns(playbooksByThoughtLeader),
    [playbooksByThoughtLeader],
  );

  const shownThoughtLeaders = useMemo((): ThoughtLeaderRow[] => {
    if (!thoughtLeaders) return [];
    const q = search.toLowerCase();
    const matches = q
      ? thoughtLeaders.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            (t.organization ?? "").toLowerCase().includes(q),
        )
      : thoughtLeaders;
    return sortListRows(
      matches,
      sort,
      direction,
      THOUGHT_LEADER_SORT_COLUMNS,
      (t) => t.name,
    );
  }, [thoughtLeaders, search, sort, direction]);

  const changeSort = (column: ThoughtLeaderSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch(
          { sort, direction },
          column,
          THOUGHT_LEADER_SORT_DEFAULTS,
        ),
      }),
    });
  };

  const createFields = (
    <>
      <form.Field name="name">
        {(nameField) => (
          <form.Field name="slug">
            {(slugField) => (
              <TitleSlugFields
                create={create}
                idPrefix="tl"
                titleLabel="Name"
                title={nameField.state.value}
                slug={slugField.state.value}
                titlePlaceholder="e.g., Alex Hormozi"
                slugPlaceholder="e.g., alex-hormozi"
                onTitleChange={nameField.handleChange}
                onSlugChange={slugField.handleChange}
              />
            )}
          </form.Field>
        )}
      </form.Field>
      <div className="grid grid-cols-2 gap-4">
        <form.Field name="organization">
          {(field) => (
            <div>
              <Label htmlFor="tl-org" className="mb-1">
                Organization
              </Label>
              <Input
                id="tl-org"
                type="text"
                value={field.state.value}
                onValueChange={field.handleChange}
                placeholder="e.g., Acquisition.com"
              />
            </div>
          )}
        </form.Field>
        <form.Field name="website">
          {(field) => (
            <div>
              <Label htmlFor="tl-website" className="mb-1">
                Website
              </Label>
              <Input
                id="tl-website"
                type="text"
                value={field.state.value}
                onValueChange={field.handleChange}
                placeholder="https://..."
              />
            </div>
          )}
        </form.Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <form.Field name="ipSensitivity">
          {(field) => (
            <div>
              <Label htmlFor="tl-ip" className="mb-1">
                IP sensitivity
              </Label>
              <Select
                value={field.state.value}
                onValueChange={(val) =>
                  field.handleChange(val as IpSensitivity)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IP_SENSITIVITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </form.Field>
        <form.Field name="partnerTier">
          {(field) => (
            <div>
              <Label htmlFor="tl-tier" className="mb-1">
                Partner tier{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </Label>
              <Select
                value={field.state.value || "__none__"}
                onValueChange={(val) =>
                  field.handleChange(
                    val === "__none__" ? "" : (val as PartnerTier),
                  )
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {PARTNER_TIER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </form.Field>
      </div>
      <form.Field name="cautiousFlag">
        {(field) => (
          <div className="flex items-center gap-3">
            <Switch
              id="tl-cautious"
              checked={field.state.value}
              onCheckedChange={field.handleChange}
            />
            <Label htmlFor="tl-cautious">
              Cautious flag — attribute conservatively (high-IP author)
            </Label>
          </div>
        )}
      </form.Field>
    </>
  );

  return (
    <AdminListPage
      title="Thought Leaders"
      description="Named authors behind playbooks — no public page of their own"
      itemName="Thought Leader"
      create={create}
      createFields={createFields}
      onCreateSubmit={() => form.handleSubmit()}
      canCreate={canCreate}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search thought leaders...",
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownThoughtLeaders}
        columns={columns}
        sorts={THOUGHT_LEADER_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(search),
          noMatchDescription: "Try adjusting your search.",
          icon: Users,
          itemName: "Thought Leader",
          title: "No thought leaders yet",
          description: "Add a thought leader to attribute playbooks to.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}

function ThoughtLeaderPlaybooks({
  playbooks,
}: {
  playbooks: PlaybookListRow[];
}) {
  if (playbooks.length === 0) {
    return <span className="text-xs text-gray-400">{"—"}</span>;
  }
  return (
    <ul className="space-y-1">
      {playbooks.map((playbook) => (
        <li key={playbook.id}>
          <Link
            to="/admin/cih/playbooks/$playbookId"
            params={{ playbookId: playbook.id }}
            className="text-gray-700 hover:text-brand-600 hover:underline"
          >
            {playbook.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}
